/*
 * Purpose: MCP secret naming + connection helpers
 * Last modified: 2026-06-28 | Agent
 * Completeness: 92
 */
import { resolveSecret, hasResolvedSecret } from '@/lib/resolveSecret';
import { useMcpStore } from '@/stores/mcpStore';

export function slugifyMcpId(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32);
  return slug || 'server';
}

/** Vault keys for the primary MCP server. */
export const MCP_DEFAULT_URL_SECRET = 'MCP_SERVER_URL';
export const MCP_DEFAULT_TOKEN_SECRET = 'MCP_AUTH_TOKEN';

export function urlSecretForMcpId(id: string): string {
  if (id === 'default') return MCP_DEFAULT_URL_SECRET;
  return `MCP_${id.toUpperCase().replace(/-/g, '_')}_URL`;
}

export function tokenSecretForMcpId(id: string): string {
  if (id === 'default') return MCP_DEFAULT_TOKEN_SECRET;
  return `MCP_${id.toUpperCase().replace(/-/g, '_')}_TOKEN`;
}

export function getMcpServerUrl(id: string): string | null {
  const record = useMcpStore.getState().getById(id);
  const secretName = record?.urlSecret ?? urlSecretForMcpId(id);
  return resolveSecret(secretName);
}

export function getMcpAuthToken(id: string): string | null {
  const record = useMcpStore.getState().getById(id);
  const secretName = record?.tokenSecret ?? tokenSecretForMcpId(id);
  return resolveSecret(secretName);
}

export function isMcpServerConnected(id: string): boolean {
  const record = useMcpStore.getState().getById(id);
  return Boolean(record?.connectedAt && getMcpServerUrl(id));
}

export function hasAnyMcpConnected(): boolean {
  return useMcpStore.getState().servers.some((s) => isMcpServerConnected(s.id));
}

export function hasMcpServerConfigured(id: string): boolean {
  const record = useMcpStore.getState().getById(id);
  const secretName = record?.urlSecret ?? urlSecretForMcpId(id);
  return hasResolvedSecret(secretName);
}
